import { useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import {
  searchLocations,
  clearLocations,
  searchIssues as reduxSearchIssues,
} from '../../../../../../redux/slices/issueSlice';
import { createPost } from '../../../../../../redux/slices/postSlice';
import { getDisplayName } from '../../../../../../utils/userDisplay.js';
import { extractMetadata } from '../../../../../../utils/linkPreview';
import { isValidUrl } from '../../utils/urlHelpers';
import { validatePostForm } from '../../utils/validation';

/**
 * Form handlers hook for PostBoxModal
 */
export const usePostBoxHandlers = (state, dispatch, onClose, userData) => {
  const {
    step, setStep,
    tag, setTag,
    issue, setIssue,
    selectedScale, setSelectedScale,
    location, setLocation,
    selectedLocation, setSelectedLocation,
    title, setTitle,
    content, setContent,
    images, setImages,
    setLocationSearchTerm,
    setIssueSearchTerm,
    setIsSubmitting,
    setIsSearchingLocation,
    setLocationError,
    selectedIssueStatus, setSelectedIssueStatus,
    setLink,
    setLinkError,
    setLinkPreview,
    setIsLoadingPreview,
    lastFetchedUrl,
    link,
    linkPreview,
  } = state;

  // Debounced issue search
  const debouncedIssueSearch = useMemo(() => debounce((term) => {
    dispatch(reduxSearchIssues(term));
  }, 300), [dispatch]);

  const handleIssueSearchChange = (e) => {
    const term = e.target.value;
    setIssueSearchTerm(term);
    debouncedIssueSearch(term);
  };

  // Debounced location search
  const debouncedLocationSearch = useMemo(() => debounce((scale, term) => {
    if (!term || term.trim() === '') {
      dispatch(clearLocations());
      return;
    }

    if (term.trim().length >= 1) {
      setIsSearchingLocation(true);
      setLocationError('');

      let searchScale;
      switch (scale) {
        case 'regional':
        case 'state':
          searchScale = 'state';
          break;
        case 'national':
          searchScale = 'country';
          break;
        case 'local':
          searchScale = 'city';
          break;
        default:
          searchScale = scale;
      }

      dispatch(searchLocations({ scale: searchScale, searchTerm: term }))
        .unwrap()
        .then(() => setIsSearchingLocation(false))
        .catch(error => {
          setIsSearchingLocation(false);
          setLocationError('Error searching locations. Please try again.');
          console.error('Location search error:', error);
        });
    } else {
      dispatch(clearLocations());
    }
  }, 200), [dispatch, setIsSearchingLocation, setLocationError]);

  const handleLocationSearch = (e) => {
    const term = e.target.value || '';
    setLocationSearchTerm(term);

    if (!term || term.trim() === '') {
      dispatch(clearLocations());
      setIsSearchingLocation(false);
      setLocationError('');
    } else {
      setIsSearchingLocation(true);
      setLocationError('');
    }

    if (
      term && term.trim() !== '' &&
      selectedLocation && term !== selectedLocation.name &&
      (!selectedLocation.region || !term.includes(selectedLocation.region))
    ) {
      setIsSearchingLocation(true);
    }

    debouncedLocationSearch(selectedScale, term);
  };

  const handleLocationSelect = (loc) => {
    setLocation(loc.name);
    if (loc.region) {
      setLocationSearchTerm(`${loc.name}, ${loc.region}`);
    } else {
      setLocationSearchTerm(loc.name);
    }
    setSelectedLocation(loc);
    dispatch(clearLocations());
    setStep(4);
  };

  // Debounced link preview fetch
  const debouncedFetchPreview = useMemo(() => 
    debounce(async (url) => {
      if (!url || lastFetchedUrl.current === url) return;
      lastFetchedUrl.current = url;
      setIsLoadingPreview(true);
      try {
        const metadata = await extractMetadata(url);
        if (metadata) {
          setLinkPreview(metadata);
          if (!link) setLink(url);
        }
      } catch (error) {
        console.error('Error fetching link preview:', error);
      } finally {
        setIsLoadingPreview(false);
      }
    }, 800),
    [link, setLink, setLinkPreview, setIsLoadingPreview, lastFetchedUrl]
  );

  const resetPostBox = () => {
    setStep(1);
    setTag(null);
    setIssue(null);
    setSelectedScale(null);
    setLocation("");
    setSelectedLocation(null);
    setTitle("");
    setContent("");
    setImages([]);
    setLocationSearchTerm('');
    setIssueSearchTerm('');
    dispatch(clearLocations());
    setSelectedIssueStatus('');
    setLink("");
    setLinkError(false);
    setLinkPreview(null);
    lastFetchedUrl.current = null;
  };

  const handleClose = () => {
    resetPostBox();
    onClose();
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    const validationError = validatePostForm({
      tag, issue, selectedScale, location, title, content, selectedIssueStatus,
    });

    if (validationError) {
      toast.warning(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      let locationData = location;
      if (selectedLocation) {
        locationData = {
          name: selectedLocation.name,
          id: selectedLocation.id,
          region: selectedLocation.region || '',
          type: selectedLocation.type || selectedScale,
          coordinates: selectedLocation.lat && selectedLocation.lon ? {
            lat: selectedLocation.lat,
            lon: selectedLocation.lon
          } : null,
          countryCode: selectedLocation.countryCode || ''
        };
      }

      const imageUrls = images || [];
      const usernameResolved = getDisplayName({
        profile: userData || null,
        authUser: userData ? { displayName: userData.displayName, email: userData.email } : null,
        candidates: [userData?.username, userData?.displayName, userData?.name]
      });

      const postData = {
        title: title.trim(),
        content: content.trim(),
        actionTag: tag,
        issue: issue,
        scale: selectedScale,
        location: locationData,
        username: usernameResolved,
        userProfession: userData?.userProfession || userData?.profession || '',
        userPhotoURL: userData?.photoURL || userData?.userPhotoURL || '',
        userId: userData?.uid || null,
        issueStatus: selectedIssueStatus,
        imageUrls: imageUrls,
        imageUrl: imageUrls.length > 0 ? imageUrls[0] : '',
        link: link && isValidUrl(link) ? link : '',
        linkPreviewData: linkPreview ? {
          url: linkPreview.url,
          title: linkPreview.title || '',
          description: linkPreview.description || '',
          image: linkPreview.image || '',
          domain: linkPreview.domain || ''
        } : null,
        isLinkPost: !!(linkPreview || (link && isValidUrl(link))),
        timestamp: new Date(),
      };

      await dispatch(createPost(postData)).unwrap();
      toast.success(`"${title}" posted successfully!`);
      handleClose();
    } catch (error) {
      console.error("Error adding post:", error);
      toast.error(error.message || "Failed to create post. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    debouncedFetchPreview,
    handleIssueSearchChange,
    handleLocationSearch,
    handleLocationSelect,
    handleClose,
    handleBack,
    handleSubmit,
    resetPostBox,
  };
};

export default usePostBoxHandlers;
